import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createInvitation, listInvitations, revokeInvitation } from '../../api/invitations.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'

/**
 * Admin — Clinician invitations.
 *
 * Admin enters the invitee's name/email/licence/specialty, submits, and
 * gets back an invite URL to share. Invitations expire in 48 hours and
 * can be revoked early. When the invitee accepts, their status flips to
 * "accepted" here.
 */
export default function InvitationsPage() {
  const qc = useQueryClient()
  const invitesQ = useQuery({
    queryKey: ['doctor-invitations'],
    queryFn: listInvitations,
  })

  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'doctor',
    specialty: '',
    license_number: '',
    place_of_work: '',
    note: '',
  })
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: (payload) => createInvitation(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-invitations'] })
      setForm({
        email: '', first_name: '', last_name: '',
        user_type: 'doctor', specialty: '',
        license_number: '', place_of_work: '', note: '',
      })
    },
  })

  const revoke = useMutation({
    mutationFn: (id) => revokeInvitation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor-invitations'] }),
  })

  async function copyLink(url) {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Silent — clipboard permissions vary by browser. The URL is
      // visible in the row so the admin can copy manually.
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Invite a clinician"
          subtitle={
            'Clinicians don’t self-register. Fill this out, share the invite link ' +
            'with the named clinician, and they’ll set their own password on accept.'
          }
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput label="First name *" value={form.first_name} onChange={set('first_name')} autoFocus />
          <LabeledInput label="Last name *" value={form.last_name} onChange={set('last_name')} />
          <LabeledInput label="Email *" type="email" value={form.email} onChange={set('email')} />
          <LabeledSelect label="Role" value={form.user_type} onChange={set('user_type')}>
            <option value="doctor">Doctor</option>
            <option value="specialist">Specialist</option>
          </LabeledSelect>
          <LabeledInput label="Specialty" value={form.specialty} onChange={set('specialty')} placeholder="e.g. General Practice, Paediatrics" />
          <LabeledInput label="MSB-PNG licence" value={form.license_number} onChange={set('license_number')} />
          <LabeledInput label="Place of work" value={form.place_of_work} onChange={set('place_of_work')} />
        </div>
        <label className="block mt-3">
          <span className="text-xs text-slate-600">Internal note (optional)</span>
          <textarea
            rows={2}
            value={form.note}
            onChange={set('note')}
            placeholder="Visible to admins only"
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
          />
        </label>

        {create.isError && <div className="mt-3"><ErrorBanner error={create.error} /></div>}

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => create.mutate(form)}
            disabled={create.isPending || !form.first_name.trim() || !form.last_name.trim() || !form.email.trim()}
            className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
          >
            {create.isPending ? 'Creating invitation…' : 'Create invitation'}
          </button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Invitations" subtitle="Pending, accepted, expired and revoked" />
        {invitesQ.error && <ErrorBanner error={invitesQ.error} onRetry={() => invitesQ.refetch()} />}
        <ul className="divide-y divide-slate-100">
          {invitesQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
          {!invitesQ.isLoading && (invitesQ.data || []).length === 0 && (
            <li className="py-3 text-sm text-slate-400">No invitations yet.</li>
          )}
          {(invitesQ.data || []).map((inv) => (
            <li key={inv.id} className="py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    Dr {inv.first_name} {inv.last_name}
                    <span className="text-slate-500 ml-2 text-xs">&lt;{inv.email}&gt;</span>
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(inv.effective_status)}`}>
                      {inv.effective_status}
                    </span>
                    <span>{inv.user_type}</span>
                    {inv.specialty && <span>{inv.specialty}</span>}
                    {inv.place_of_work && <span>{inv.place_of_work}</span>}
                    {inv.license_number && <span>Lic. {inv.license_number}</span>}
                    <span>Invited {formatDateTime(inv.created_at)}</span>
                    {inv.expires_at && <span>Expires {formatDateTime(inv.expires_at)}</span>}
                  </div>
                  {inv.effective_status === 'pending' && inv.invite_url && (
                    <div className="mt-2 text-xs flex items-center gap-2 font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1 truncate max-w-full">
                      <a href={inv.invite_url} target="_blank" rel="noreferrer" className="text-wantok-700 underline truncate">
                        {inv.invite_url}
                      </a>
                      <button
                        type="button"
                        onClick={() => copyLink(inv.invite_url)}
                        className="text-slate-500 underline decoration-dotted shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                </div>
                {inv.effective_status === 'pending' && (
                  <button
                    onClick={() => revoke.mutate(inv.id)}
                    disabled={revoke.isPending}
                    className="text-xs text-rose-700 underline shrink-0"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}

function LabeledInput({ label, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <input {...rest} className="border rounded px-2 py-1.5 text-sm" />
    </label>
  )
}

function LabeledSelect({ label, children, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <select {...rest} className="border rounded px-2 py-1.5 text-sm bg-white">
        {children}
      </select>
    </label>
  )
}
